/**
 * The bound on SurveySession's option-media paint/failure trackers.
 *
 * Its own module rather than an export from SurveySession.tsx: `react-refresh/only-export-components`
 * flags a component file that also exports a function, and the rule is right here — a shared helper
 * with its own unit tests does not belong behind a 5,000-line component.
 */

/**
 * The two namespaces an option-media key can be written in, and the reason they live HERE rather
 * than beside the builders that use them: the prune below is only a bound while it anchors on
 * them, so the namespace and the anchor have to be one fact. Split across two modules they can
 * drift, and a drifted anchor does not fail loudly — it silently keeps everything or nothing.
 *
 * `SurveySession` imports both for its key builders; nothing else writes these trackers.
 */
export const CHOICE_OPTION_MEDIA_KEY_PREFIX = 'choice-option-';
export const MAX_DIFF_ITEM_MEDIA_KEY_PREFIX = 'maxdiff-item-';

const OPTION_MEDIA_KEY_PREFIXES = [
  CHOICE_OPTION_MEDIA_KEY_PREFIX,
  MAX_DIFF_ITEM_MEDIA_KEY_PREFIX,
] as const;

/**
 * Whether one key is one question's, ANCHORED AT BOTH ENDS.
 *
 * A key is `${namespace}${questionId}@${visit}#${set}-…`, so the question id occupies a known
 * span: it starts where the namespace ends and stops at the `@`. Testing for `${questionId}@`
 * anywhere in the key holds only the right edge, and `makeQuestionVisitKey` types its id
 * `string | number` — with numeric ids `1@` is inside every key of questions `11`, `21`, `31`, so
 * question 1 kept theirs for the whole session and counted them into a gate that is a COUNT over
 * the tracker. Anchoring the left edge on the delimiter instead (`-${questionId}@`) just moves the
 * hole: ids are free-form and hyphens are ordinary in them, so `-1@` sits inside `block-1`'s keys.
 * The namespace is the only left edge that cannot appear mid-key.
 */
const isMarkOfQuestion = (key: string, questionId: string): boolean =>
  OPTION_MEDIA_KEY_PREFIXES.some((prefix) => key.startsWith(`${prefix}${questionId}@`));

/**
 * The option-media marks that name one question, out of a tracker holding every question's.
 *
 * Every option-media key embeds `${questionId}@${visit}#${set}` (see optionMediaScopeKey), so a
 * question's marks are recognisable — and the anchoring above is what keeps `q1` from claiming
 * `q10`'s and `1` from claiming `11`'s, which matters because the choice gate is a COUNT over the
 * tracker.
 *
 * Returns the SAME object when nothing is dropped, so the common case — a question whose marks are
 * all its own — does not commit a new state value and re-render for no reason. That is not a
 * micro-optimisation: the `{}` reset this replaces committed a new object unconditionally, and the
 * extra commit per question change is what made the SurveySession suite flake under load.
 */
export const keepMarksForQuestion = (
  marks: Record<string, boolean>,
  questionId: string,
): Record<string, boolean> => {
  const keys = Object.keys(marks);
  // No question on screen (the frames before the survey loads) owns no marks. Kept explicit rather
  // than left to the anchor: `${namespace}@` is a key an id-less question could genuinely write.
  const kept = questionId === '' ? [] : keys.filter((key) => isMarkOfQuestion(key, questionId));
  if (kept.length === keys.length) return marks;
  return Object.fromEntries(kept.map((key) => [key, marks[key]]));
};
